import { KICK_CODES, type KickCode, type ServerMessage, type StateMessage, type WelcomeMessage } from "../types";

type MessageOf<K extends ServerMessage["t"]> = Extract<ServerMessage, { t: K }>;
type Listener<K extends ServerMessage["t"]> = (msg: MessageOf<K>) => void;

/** Eventos de conexao, alem das mensagens do protocolo. */
export interface ConnectionEvents {
  open: () => void;
  close: (reason: string) => void;
  reconnecting: (attempt: number) => void;
}

const PING_INTERVAL = 3000;
const MAX_RECONNECT_DELAY = 15000;

/**
 * Conexao com o mundo. Reconecta sozinha com backoff exponencial; ao voltar,
 * o servidor manda um novo `welcome` e a cena reconstroi a lista de jogadores.
 */
/** Teto da fila de mensagens sem ouvinte (cobre alguns segundos de snapshots). */
const BACKLOG_LIMIT = 200;

export class SocketClient {
  private ws: WebSocket | null = null;
  private readonly listeners = new Map<string, Set<(msg: never) => void>>();
  private readonly connListeners = new Map<keyof ConnectionEvents, Set<(...args: never[]) => void>>();
  private reconnectAttempt = 0;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private disposed = false;
  /** dispose() fechou o socket de proposito (ex.: reconectar apos trocar o avatar). */
  private closedByUs = false;
  /** Mensagens recebidas antes de haver ouvinte para o tipo delas. */
  private backlog: ServerMessage[] = [];

  /** Round-trip medido pelo par ping/pong, em milissegundos. */
  latency = 0;

  constructor(private readonly token: string) {}

  private url(): string {
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${location.host}/ws?token=${encodeURIComponent(this.token)}`;
  }

  /** Abre a conexao e resolve com o primeiro `welcome` recebido. */
  connect(): Promise<WelcomeMessage> {
    return new Promise((resolve, reject) => {
      let settled = false;

      const off = this.on("welcome", (msg) => {
        if (settled) return;
        settled = true;
        off();
        offSystem();
        resolve(msg);
      });

      const offClose = this.onConnection("close", (reason) => {
        if (settled) return;
        settled = true;
        off();
        offClose();
        offSystem();
        reject(new Error(reason));
      });

      // Derrubado logo na entrada (ex.: conta desativada): o motivo chega
      // antes do fechamento e vale mais que o "conexao perdida".
      const offSystem = this.on("system", (msg) => {
        if (settled || !KICK_CODES.includes(msg.code as KickCode)) return;
        settled = true;
        off();
        offClose();
        offSystem();
        this.stopReconnecting();
        reject(new Error(msg.message));
      });

      this.open();
    });
  }

  private open(): void {
    if (this.disposed) return;

    const ws = new WebSocket(this.url());
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempt = 0;
      this.emitConnection("open");
      this.startPing();
    };

    ws.onmessage = (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string) as ServerMessage;
      } catch {
        console.warn("mensagem inválida do servidor", event.data);
        return;
      }

      if (msg.t === "pong") {
        this.latency = Math.max(0, Date.now() - msg.ts);
        return;
      }
      this.emit(msg);
    };

    ws.onerror = () => {
      // O evento de erro do WebSocket nao traz detalhes; o onclose logo a
      // seguir e quem carrega o motivo util.
    };

    ws.onclose = (event) => {
      this.stopPing();
      this.ws = null;
      // Fechamento pedido por nos: nao e queda, nao avisa nem reconecta.
      if (this.closedByUs) return;

      const reason =
        event.code === 1008 || event.code === 1006
          ? "Conexão recusada pelo servidor. Sua sessão pode ter expirado."
          : "Conexão com o mundo perdida.";

      this.emitConnection("close", reason);
      if (!this.disposed) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    this.reconnectAttempt += 1;
    const delay = Math.min(1000 * 2 ** (this.reconnectAttempt - 1), MAX_RECONNECT_DELAY);
    this.emitConnection("reconnecting", this.reconnectAttempt);
    this.reconnectTimer = window.setTimeout(() => this.open(), delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.send({ t: "ping", ts: Date.now() });
    }, PING_INTERVAL);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  // ------------------------------------------------------------- emissao

  private send(payload: object): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  sendInput(dx: number, dy: number, seq: number): void {
    this.send({ t: "input", dx, dy, seq });
  }

  sendChat(text: string): void {
    this.send({ t: "chat", text });
  }

  sendInteract(): void {
    this.send({ t: "interact" });
  }

  /** Comeca um gesto em loop; "" para. */
  sendGesture(id: string): void {
    this.send({ t: "gesture", g: id });
  }

  // chat de voz (ver server/internal/world/voice.go)

  /** Pede um token novo (a conexao com o LiveKit caiu). */
  requestVoice(): void {
    this.send({ t: "voice" });
  }

  lockVoice(): void {
    this.send({ t: "voice_lock" });
  }

  unlockVoice(): void {
    this.send({ t: "voice_unlock" });
  }

  /** Pede para entrar na conversa trancada `locked`. */
  knockVoice(locked: string): void {
    this.send({ t: "voice_knock", b: locked });
  }

  /** Deixa `id` (quem bateu) entrar na conversa. */
  admitVoice(id: string): void {
    this.send({ t: "voice_admit", id });
  }

  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Desiste de reconectar, mas mantem a conexao atual se ainda estiver de pe.
   * Usado quando o servidor encerra a sessao de proposito (a conta abriu em
   * outra aba): insistir so faria as duas abas se derrubarem em looping.
   */
  stopReconnecting(): void {
    this.disposed = true;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  dispose(): void {
    this.closedByUs = true;
    this.stopReconnecting();
    this.stopPing();
    this.ws?.close(1000, "saindo");
    this.ws = null;
  }

  // ------------------------------------------------------------- eventos

  /** Registra um ouvinte e devolve a funcao que o remove. */
  on<K extends ServerMessage["t"]>(type: K, listener: Listener<K>): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(listener as (msg: never) => void);

    // Entrega o que chegou antes de existir ouvinte deste tipo, na ordem.
    const queued = this.backlog.filter((msg) => msg.t === type);
    if (queued.length > 0) {
      this.backlog = this.backlog.filter((msg) => msg.t !== type);
      for (const msg of queued) (listener as (m: ServerMessage) => void)(msg);
    }
    return () => set.delete(listener as (msg: never) => void);
  }

  onConnection<K extends keyof ConnectionEvents>(event: K, listener: ConnectionEvents[K]): () => void {
    let set = this.connListeners.get(event);
    if (!set) {
      set = new Set();
      this.connListeners.set(event, set);
    }
    set.add(listener as (...args: never[]) => void);
    return () => set.delete(listener as (...args: never[]) => void);
  }

  private emit(msg: ServerMessage): void {
    const set = this.listeners.get(msg.t);
    if (!set || set.size === 0) {
      // Ninguem escutando ainda: entre o welcome e o GardenScene subir (preload
      // dos assets) chega o primeiro state, com o `joined` de quem ja estava
      // no Jardim — e o servidor nao manda de novo. Guardamos para entregar.
      this.enqueue(msg);
      return;
    }
    for (const listener of set) {
      (listener as (m: ServerMessage) => void)(msg);
    }
  }

  /**
   * Snapshots `state` sao fundidos num so (entrada/saida acumuladas, posicoes
   * do mais recente), entao uma carga lenta nao perde ninguem nem cresce sem
   * limite. Os outros tipos ficam em ordem; passando do teto, sai o mais velho.
   */
  private enqueue(msg: ServerMessage): void {
    if (msg.t === "state") {
      const i = this.backlog.findIndex((m) => m.t === "state");
      if (i >= 0) {
        this.backlog[i] = mergeStates(this.backlog[i] as StateMessage, msg);
        return;
      }
    }
    this.backlog.push(msg);
    if (this.backlog.length > BACKLOG_LIMIT) {
      const oldest = this.backlog.findIndex((m) => m.t !== "state");
      this.backlog.splice(oldest >= 0 ? oldest : 0, 1);
    }
  }

  private emitConnection<K extends keyof ConnectionEvents>(
    event: K,
    ...args: Parameters<ConnectionEvents[K]>
  ): void {
    const set = this.connListeners.get(event);
    if (!set) return;
    for (const listener of set) {
      (listener as (...a: unknown[]) => void)(...args);
    }
  }
}

/**
 * Junta dois snapshots seguidos como se o cliente tivesse processado os dois:
 * quem entrou e saiu no meio some, quem saiu e voltou fica, e as posicoes,
 * o tick e o contador de online sao os do mais novo.
 */
function mergeStates(older: StateMessage, newer: StateMessage): StateMessage {
  const joined = new Map((older.joined ?? []).map((p) => [p.id, p]));
  const left = new Set(older.left ?? []);
  for (const id of newer.left ?? []) {
    if (joined.delete(id)) continue; // entrou e saiu dentro da fila
    left.add(id);
  }
  for (const p of newer.joined ?? []) {
    left.delete(p.id);
    joined.set(p.id, p);
  }
  return {
    ...newer,
    joined: joined.size > 0 ? [...joined.values()] : undefined,
    left: left.size > 0 ? [...left] : undefined,
    ack: newer.ack ?? older.ack,
  };
}
